import PropTypes from 'prop-types';

import isEmpty from 'lodash/fp/isEmpty';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { BasicModal } from 'Components/BasicModal';
import Error from 'Components/Error';
import ScaleLoader from 'Components/ScaleLoader';
import { Typography } from 'Components/Typography';

import { productSlugToProductHaircare } from 'assets/content/haircareProducts';
import { productSlugToProductSkincare } from 'assets/content/skincareProducts';

import { genitive } from 'utils/textUtils';

import Spacer from './Spacer';

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${theme.colors.neutral['700']};
  margin: 8px 0px;
  ${legacyTheme.breakpoints.up('md')} {
    width: ${({ isFullWidth }) => (isFullWidth ? 100 : 50)}%;
  }
`;

const ProductLabel = styled(Typography)`
  font-size: 14px;
  margin-bottom: 12px;
`;

const IngredientsList = styled(Typography)`
  color: ${theme.colors.neutral[800]};
  margin-bottom: 32px;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px;
`;

const IngredientsListModal = ({ isOpen, products, name, onClose, hasError, isLoading }) => {
  const productSlugToProduct = {
    ...productSlugToProductSkincare,
    ...productSlugToProductHaircare,
  };

  return (
    <BasicModal dataTestId="ingredients-modal-closer" isOpen={isOpen} onClose={onClose}>
      {isLoading ? (
        <LoaderContainer>
          <ScaleLoader color={theme.colors.neutral['900']} margin="5px" size="40px" />
        </LoaderContainer>
      ) : hasError ? (
        <Error reload />
      ) : (
        <>
          {products.length > 1 && (
            <>
              <Typography id={isOpen ? 'modal-title' : ''} variant="h1">{`${genitive(
                name
              )} ingredients`}</Typography>
              <Divider isFullWidth />
              <Spacer size={32} />
            </>
          )}

          <div id={isOpen ? 'modal-description' : ''}>
            {products?.map(product => {
              const productLabel = productSlugToProduct[product.type]?.label;
              return (
                <div key={product.skeleton_type}>
                  {productLabel && (
                    <ProductLabel bold variant="mono2">{`${genitive(
                      name
                    )} ${productLabel} ingredients*`}</ProductLabel>
                  )}
                  {!isEmpty(product.incis) && (
                    <IngredientsList data-testid={`${product.type}-ingredients-list`} variant="p1">
                      {product.incis?.join(', ')}
                    </IngredientsList>
                  )}
                  {products.length > 1 && (
                    <>
                      <Divider />
                      <Spacer size={32} />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
      {products.length > 1 && <Spacer size={32} />}
      <Typography color="grey" id={isOpen ? 'modal-condition' : ''} italic variant="p3">
        *This is a close approximation of the ingredients in your custom formula. Because our
        products are made to order according to your needs and environment, your custom ingredients
        may vary slightly from what’s shown here. A finalized list of your product’s ingredients
        will be included in the leaflet that ships with your order.
      </Typography>
    </BasicModal>
  );
};

IngredientsListModal.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      skeleton_type: PropTypes.string,
      incis: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  hasError: PropTypes.bool,
  isLoading: PropTypes.bool,
};

IngredientsListModal.defaultProps = {
  products: null,
  hasError: false,
  isLoading: false,
};

export default IngredientsListModal;
